import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { JadeTunnelInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * JADE TUNNEL — eighth cartridge (sc-3443).
 *
 * Side-scrolling jungle expedition with vine swings, gators, snakes,
 * scorpions, quicksand, tar pits — plus a vertical cave subsystem.
 * Pitfall I (1982) + Pitfall II: Lost Caverns (1984) heritage.
 *
 * Original mechanic — **Sun Timer**: the treasure vault closes at
 * sundown. A visible sun arc top-center progresses sunrise → noon →
 * sunset over ~90 seconds (5400 ticks) of in-jungle time. Caves PAUSE
 * the sun (you're underground); re-emerging costs +5s of drift. Scoring
 * is a risk/budget puzzle, not a twitch-skill ceiling.
 *
 * MVP build is a 6-step sequence (see sc-3443):
 *
 *   1. Skeleton — player walks left/right; scrolling jungle world.
 *      Sun renders static; no hazards, no game-over. **(this commit)**
 *   2. Sun timer + jump + pits — sun arc ticks down; jump physics;
 *      bottomless pits; game-over on sundown or out of lives.
 *   3. Vines + gators — pendulum vines + grab logic; synced gator
 *      mouth rhythm; jump-the-gator-heads traversal.
 *   4. Snakes + scorpions + quicksand + tar pits + falling logs —
 *      remaining surface hazards.
 *   5. Caves + bats — cave portals, vertical cave map, ladder climb,
 *      bat AI, sun-pause + emerge-drift penalty.
 *   6. Idol loot + scoring polish + finishing bonus + determinism test.
 *      One jade idol per cave on the bottom pedestal — +250 each.
 *      Reaching the rightmost screen before sundown wins +1000 and
 *      ends the run as TREASURE!. Headless determinism test mirrors
 *      the sibling cartridges (1000 runs of same seed → same score).
 *
 * Resolution: 480x270 pixelated. Vaporwave-jungle palette (deep
 * magenta canopy, neon-cyan vines, hot-orange treasure glow).
 */
const module_: GameModule = {
  id: 'jade-tunnel',
  title: 'JADE TUNNEL',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'platform',
  resolution: { w: 480, h: 270 },
  pixelated: true,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'LEFT / RIGHT', action: 'WALK' },
    { keys: 'DOWN', action: 'ENTER CAVE PORTAL' },
    { keys: 'UP / DOWN', action: 'CLIMB LADDER (IN CAVE)' },
    { keys: 'UP', action: 'EXIT CAVE (AT TOP PLATFORM)' },
    { keys: 'Z / SPACE', action: 'JUMP / GRAB VINE' },
    { keys: 'Z', action: 'RESTART (AT GAME OVER)' },
  ],
  touch: {
    fireA: 'JUMP',
  },

  async preload(ctx: PreloadContext): Promise<void> {
    // Jump — short rising blip. Frequent input so kept quiet + short.
    ctx.registerSfx('jump', {
      wave: 'square',
      pitch: [{ t: 0, f: 480 }, { t: 22, f: 740 }],
      gain: [{ t: 0, g: 0 }, { t: 3, g: 0.1 }, { t: 22, g: 0.0 }],
    });
    // Land — soft thunk on touch-down. Plays only when crossing from
    // airborne to grounded, so it doesn't fatigue during normal walking.
    ctx.registerSfx('land', {
      wave: 'sine',
      pitch: [{ t: 0, f: 200 }, { t: 40, f: 110 }],
      gain: [{ t: 0, g: 0 }, { t: 4, g: 0.14 }, { t: 40, g: 0.0 }],
    });
    // Death — falling cry. Longer descending noise burst with sub-bass
    // thump. Plays when the player drops into a pit (and, in later
    // steps, when other hazards kill).
    ctx.registerSfx('death', {
      wave: 'noise',
      pitch: [{ t: 0, f: 180 }, { t: 300, f: 40 }],
      gain: [{ t: 0, g: 0 }, { t: 6, g: 0.4 }, { t: 300, g: 0.0 }],
    });
    // Gator chomp — short noise crunch with low square thump. Fires
    // when the player lands on an open gator mouth (= gator-bite death).
    ctx.registerSfx('chomp', {
      wave: 'noise',
      pitch: [{ t: 0, f: 220 }, { t: 90, f: 80 }],
      gain: [{ t: 0, g: 0 }, { t: 4, g: 0.4 }, { t: 90, g: 0.0 }],
    });
    // Vine grab — quick rising chime. Plays when the player attaches
    // to a vine; reinforces the "I caught it" beat without fatiguing.
    ctx.registerSfx('vine-grab', {
      wave: 'triangle',
      pitch: [{ t: 0, f: 520 }, { t: 60, f: 880 }],
      gain: [{ t: 0, g: 0 }, { t: 4, g: 0.18 }, { t: 60, g: 0.0 }],
    });
    // Bite — generic ground-creature kill (snake / scorpion / log). Mid-
    // range noise with a quick descending pitch. Distinct from chomp
    // (gator) and death (pit fall).
    ctx.registerSfx('bite', {
      wave: 'noise',
      pitch: [{ t: 0, f: 360 }, { t: 120, f: 120 }],
      gain: [{ t: 0, g: 0 }, { t: 4, g: 0.32 }, { t: 120, g: 0.0 }],
    });
    // Idol pickup — bright triangular arpeggio. Communicates "you got
    // something good"; distinct from the more utilitarian vine-grab.
    ctx.registerSfx('idol-pickup', {
      wave: 'triangle',
      pitch: [
        { t: 0, f: 660 },
        { t: 40, f: 990 },
        { t: 80, f: 1320 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 4, g: 0.22 },
        { t: 80, g: 0.0 },
      ],
    });
  },

  init(ctx: GameContext): GameInstance {
    return new JadeTunnelInstance(ctx);
  },
};

export default module_;
